// MARK://dhk8/admin-shell/lib/clients/recipes
// Onboarding plumbing:
//   • ingredients (cost + unit) → dhk8-erp-products `/v1/ingredients` (a Product type=INGREDIENT
//     whose priceCents = costCents, unit = unit of measure)
//   • the sellable product (type=PRODUCT) → dhk8-erp-products `/v1/products` (via catalog.ts)
//   • the recipe (qty per portion, linking the product to its ingredient-products) →
//     dhk8-catalog-recipes `/v1/recipes` + `/v1/recipes/:id/ingredients`
//   • "portion groups" (cuántos tacos por porción) → erp-products `addProductVariant`
//     (grafo, F6.2 R4) — see note on createPortionGroup.
// REST + PREVIEW_STUB mock fallback throughout.
import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import * as M from '../mock-data.js'
import { createProduct, createVariant } from './catalog.js'
import { createRecipe, addIngredient } from './recipes-catalog.js'
import type { ApiProduct } from './catalog.js'

const db = { ingredients: structuredClone(M.ONBOARDING_INGREDIENTS) }
const mkid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const DEFAULT_INGREDIENTS = M.ONBOARDING_INGREDIENTS
export const DEFAULT_RECIPE = M.ONBOARDING_RECIPE

const PRODUCTS_SVC = 'dhk8-erp-products'

/** Create an ingredient = a Product flagged type=INGREDIENT carrying its cost-per-unit. */
export async function createIngredient(input: Omit<M.Ingredient, 'id'>): Promise<M.Ingredient> {
  if (PREVIEW_STUB) { const ing = { ...input, id: mkid('ing') }; db.ingredients.push(ing); return mock(ing) }
  const row = await apiData<ApiProduct>(svcUrl(PRODUCTS_SVC, '/v1/ingredients'), {
    method: 'POST',
    body: { tenantId: authTenantId(), name: input.name, unit: input.unit || 'PIEZA', costCents: input.costCents },
  })
  return { id: row.id, name: row.name, unit: row.unit ?? input.unit, costCents: row.priceCents }
}

/** Cost of one portion given the recipe lines + ingredient costs (in cents). */
export function portionCostCents(recipe: M.RecipeLine[], ingredients: M.Ingredient[]): number {
  return Math.round(recipe.reduce((sum, line) => {
    const ing = ingredients.find((i) => i.id === line.ingredientId)
    return sum + (ing ? ing.costCents * line.qtyPerPortion : 0)
  }, 0))
}

/**
 * Persist a recipe-backed sellable product:
 *   1. create the Product (type=PRODUCT) via dhk8-erp-products with the sale price
 *   2. create the Recipe in dhk8-catalog-recipes linked to that productId
 *   3. add each recipe line as a recipe ingredient (only those whose ingredientId is a
 *      real product UUID — onboarding's sample lines may still hold mock ids if the
 *      ingredients weren't re-created with real ids yet; those are skipped, not fatal)
 * Returns the created product.
 *
 * The caller (OnboardingPage) derives `sku` from the product name (e.g. "Tacos al
 * pastor" → "TACOSAL"), which collides with the dhk8_erp.product
 * @@unique([tenantId, sku]) on retry or when two onboardings share the default
 * product name. We append a short readable base36 suffix here so the SKU stays
 * meaningful but is virtually guaranteed unique within the tenant.
 */
export async function createRecipeProduct(args: {
  productName: string
  sku: string
  categoryId: string
  recipe: M.RecipeLine[]
  ingredients: M.Ingredient[]
  salePriceCents: number
}): Promise<M.Product> {
  const baseSku = (args.sku || 'PROD').toUpperCase()
  const uniqueSuffix = Date.now().toString(36).slice(-5).toUpperCase()
  const sku = `${baseSku}-${uniqueSuffix}`
  const product = await createProduct({
    name: args.productName,
    sku,
    priceCents: args.salePriceCents,
    categoryId: args.categoryId,
    available: true,
    taxRate: '0.16',
  })
  if (PREVIEW_STUB) return mock(product)

  // F6.2 R1 / ADR-0008: alta de receta + ingredientes por el grafo (catalog-recipes
  // `createRecipe`/`addRecipeIngredient`, tenant del JWT, RLS). Sustituye los POST
  // REST /v1/recipes y /v1/recipes/:id/ingredients. addIngredient ya normaliza la
  // cantidad a string decimal `^\d+(\.\d{1,4})?$` (qtyStr) en el cliente.
  const recipe = await createRecipe({ productId: product.id, name: `Receta · ${args.productName}`, yieldServings: 1 })
  for (const line of args.recipe) {
    if (!UUID_RE.test(line.ingredientId)) continue // mock id — skip (TODO: re-create ingredients with real ids in the wizard)
    const ing = args.ingredients.find((i) => i.id === line.ingredientId)
    await addIngredient(recipe.id, { productId: line.ingredientId, quantity: line.qtyPerPortion, unit: ing?.unit || 'PIEZA' })
      .catch(() => undefined) // a single bad line shouldn't sink the whole product
  }
  return product
}

/**
 * Create a "size/portion" variant for the product (e.g. "4 tacos" = 4× the base
 * portion, optionally with a discount).
 *
 * F6.2 R4 / ADR-0008: persistido por el grafo vía erp-products `addProductVariant`
 * (catalog.ts `createVariant`, product-scoped, IDOR-guard por tenant del JWT).
 * Sustituye el POST REST legacy a dhk8-catalog-variants `/v1/products/:id/variants`,
 * que además estaba desalineado de su propio schema (mandaba priceCents/attributes
 * que ese servicio no acepta). El modelo de variantes canónico es dhk8_erp.ProductVariant.
 * If `productId` isn't a real UUID (onboarding hasn't captured the just-created product
 * id yet) this no-ops and just returns the computed price.
 */
export async function createPortionGroup(args: {
  productId: string
  groupName: string          // "Cuántos tacos por porción"
  optionName: string         // "4 tacos"
  multiplier: number         // 4 → 4× the base portion
  basePortionPriceCents: number
  discount: { kind: 'none' | 'percent' | 'amount'; value: number }
}): Promise<{ priceCents: number }> {
  const gross = args.basePortionPriceCents * args.multiplier
  const priceCents = args.discount.kind === 'percent'
    ? Math.round(gross * (1 - args.discount.value / 100))
    : args.discount.kind === 'amount'
      ? Math.max(0, gross - args.discount.value)
      : gross
  if (PREVIEW_STUB) return mock({ priceCents })
  if (!UUID_RE.test(args.productId)) return { priceCents } // no real product to attach to yet
  // F6.2 R4 / ADR-0008: alta de variante por el grafo (erp-products, tenant del JWT).
  // priceCents (absoluto, ya calculado con multiplier+descuento) viaja como el precio
  // de la variante; el nombre identifica la opción ("4 tacos"). Una sola línea mala no
  // debe tumbar el wizard, así que swallow el error como hacía el POST REST previo.
  await createVariant({ productId: args.productId, name: args.optionName, priceDeltaCents: priceCents }).catch(() => undefined)
  return { priceCents }
}
